import OnlineAppBase from './lib/online_app_base';

import {OnlineAppRouter} from "./online_app/routing/online_app_router";

import {Prefetcher} from "./services/prefetcher";
import {ExperienceMarkersLoader} from "./services/experience_markers_loader";

import {OnlineAppBootingConfig, getBooting} from "./components/online_app_booting";
import {OnlineAppTitleConfig, getTitle} from "./components/online_app_title";
import {OnlineAppFooterConfig, getFooter} from "./components/online_app_footer";
import {OnlineAppMainConfig, getMain} from "./components/online_app_main";
import {OnlineAppMapConfig, OnlineAppMap} from "./components/online_app_map";
import {OnlineAppNav, OnlineAppNavConfig} from "./components/online_app_nav";
import {OnlineAppAuxConfig, getAux} from "./components/online_app_aux";
import {OnlineAppSubmarker} from "./components/online_app_submarker";

import Collection from "../lib/collection";
import ExperienceMarkerCollection from './lib/experience_marker_collection';
import ExperienceCollection from './lib/experience_collection';
import MapFilterCollection from './lib/map_filter_collection';
import {isEmpty} from '../helpers/object';
import {escapeForRegExp} from "../helpers/string";
import {injectLibrary, injectStylesheet} from "../helpers/injection";
import Tracker from "../lib/tracker";
import {getProgress, OnlineAppProgressConfig} from "./components/online_app_progress";
import {getMapOverlay} from "./components/online_app_map_overlay";
import {valueFromUrlParams} from "../helpers/url";
import Nodalizer from "../lib/nodalizer";
import {getHeader, OnlineAppHeaderConfig} from "./components/online_app_header";
import {getStatus, OnlineAppStatusConfig} from "./components/online_app_status";

import {NavConfig} from "./nav/nav";
import SubpageCollection from "./lib/subpage_collection";

export class OnlineApp extends OnlineAppBase {

    constructor(onlineAppRoot, props) {
        super(props);

        this.root = onlineAppRoot; // The root component of the app.

        this.name = props.name;
        this.code = props.code;
        this.slug = props.slug;
        this.version = props.version;
        this.cacheKey = props.cache_key;
        this.basePageTitle = props.basePageTitle;
        this.protocol = props.protocol;
        this.host = props.host;
        this.basePath = props.base_path;
        this.googleMapsApiKey = props.google_maps_api_key;
        this.apiExperiencesPath = props.api_experiences_path;
        this.navPath = props.nav_path;
        this.auxiliaryScripts = props.auxiliary_scripts;
        this.auxiliaryStylesheets = props.auxiliary_stylesheets;
        this.defaultMapFilterCode = props.default_map_filter_code;

        this.regionCode = valueFromUrlParams('region_code');
        this.mapPosition = { lat: valueFromUrlParams('lat', 'float'), long: valueFromUrlParams('long', 'float') };
        this.mapZoom = valueFromUrlParams('zoom', 'integer');

        this.currentMapFilter = null;
        this.currentExperienceMarker = null;
        this.currentSubPageUrl = null;

        this.initialPath = this.env.location.pathname;
        this.initialAppPath = this.getInitialAppPath(); // The app path is the location path without the basePath

        this.currentPath = this.env.location.pathname;

        this.isTouchDevice = ( 'ontouchstart' in this.doc.documentElement ); // Feature detect a touch device.
        this.clickActionName = this.isTouchDevice ? 'tap' : 'click';

        this.onlineAppRouter = new OnlineAppRouter(this.basePath, this);

        this.prefetcher = new Prefetcher(this.basePath, this);

        this.constructBaseComponents();

        this.experienceMarkerCollection = new ExperienceMarkerCollection(this);
        this.experienceCollection = new ExperienceCollection(this);
        this.mapFilterCollection = new MapFilterCollection(this);
        this.subpageCollection = new SubpageCollection(this);

        this.tracker = new Tracker(this.env, this.doc, { isNativeApp: false });

        this.stateMachine.setPersistentStorageKey(`${this.code}__${this.stateMachine.persistentStorageKey}`); // We want a unique store for each online app.
        this.dispatchAction("initFromPersistentStore");

        this.loadResources().catch(error => {
            console.error(`[OnlineApp] Error loading resources:`, error);
        }).finally(() => {
            this.loadAuxiliaryResources(); // Async load auxiliary non-critical scripts and stylesheets.
        });
        // See `handleNavBooted` for the rest of the boot process.
        // This is an event handler that is fired when the `Nav` component has booted.
    }

    // Instantiate the base components of the app. These are components that don't depend on any resources that will be loaded.
    constructBaseComponents() {const rootElement = this.getRootElement();
        if (!rootElement) return;
        // Order of construction is important - during a stateChange event the render methods of the components will be called in order.

        this.progress = getProgress(rootElement.querySelector(OnlineAppProgressConfig.selector), this);
        this.status = getStatus(rootElement.querySelector(OnlineAppStatusConfig.selector), this);
        this.title = getTitle(rootElement.querySelector(OnlineAppTitleConfig.selector), this);
        this.header = getHeader(rootElement.querySelector(OnlineAppHeaderConfig.selector), this);
        this.booting = getBooting(rootElement.querySelector(OnlineAppBootingConfig.selector), this);
        this.footer = getFooter(rootElement.querySelector(OnlineAppFooterConfig.selector), this);
        this.aux = getAux(rootElement.querySelector(OnlineAppAuxConfig.selector), this);
        this.main = getMain(rootElement.querySelector(OnlineAppMainConfig.selector), this);
        this.mapOverlay = getMapOverlay(rootElement.querySelector(OnlineAppMainConfig.selector), this);
    }

    async loadResources() {
        this.dispatchAction('loadResourcesStarted');

        // Wait specifically for Google Maps Libraries to load.
        // Some components need to be constructed *after* the map libraries are loaded
        // Also the markers can't be initialized until the map libraries are loaded.
        // And the navigation must be loaded after the markers to ensure that markers show up when the nav selects a nav option/map filter on boot.
        await this.loadMapLibs();
        this.constructMapLibsDependentComponents();
        await this.loadMapMarkers();
        await this.loadNavigation();

        this.dispatchAction('loadResourcesEnded');
    }

    async loadMapLibs() {
        this.dispatchAction('loadMapLibsStarted');

        // Helper function to load a library and return a promise
        const injectLibraryAsync = (url) => new Promise((resolve, reject) => {
            injectLibrary(url, resolve); // Assuming injectLibrary does not have an error callback
        });

        try {
            await injectLibraryAsync(`https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}`);
            await injectLibraryAsync(`/javascripts/online/apps/infobox_packed.js?${this.cacheKey}`);
            await injectLibraryAsync(`/javascripts/online/apps/richmarker-compiled.js?${this.cacheKey}`);
        } catch (error) {
            // Handle any errors that might occur during the library loading
            this.dispatchAction('loadResourcesFailed');
            console.error('[OnlineApp] Error loading Google Libraries:', error);
        } finally {
            this.dispatchAction('loadMapLibsEnded');
        }
    }

    async loadMapMarkers() {
        try {
            this.dispatchAction('loadMarkersStarted');
            let experienceMarkers = await new ExperienceMarkersLoader(this.apiExperiencesPath, this).getExperienceMarkers();
            experienceMarkers = this.importExperienceMarkers(experienceMarkers);
            const experiences = this.importExperiences(experienceMarkers);
            this.associateNavOptions(experiences);
            this.dispatchAction('experienceMarkersLoaded');
        } catch (error) {
            this.dispatchAction('experienceMarkersLoadFailed');
            console.error(`[OnlineApp] Failed to load experience markers`);
            console.error(error);
            throw error; // Re-throw the error to be handled by the caller
        } finally {
            this.dispatchAction('loadMarkersEnded');
        }
    }


    async loadNavigation() {
        const rootElement = this.getRootElement();
        if (!rootElement) return;

        const navRootElement = rootElement.querySelector(OnlineAppNavConfig.selector); // The element to be updated with the fetched nav.
        if (this.navPath && navRootElement) {
            this.dispatchAction("loadNavStarted");

            const nodalizerMapping = {};
            nodalizerMapping[OnlineAppNavConfig.selector] = navRootElement;
            const nodalizer = new Nodalizer(this.navPath, nodalizerMapping);

            try {
                // Await the promise returned by insertIntoDOM
                await nodalizer.insertIntoDOM();
                // Now the nav has been inserted into the DOM.
                // However, the `Nav` instance (see `app/javascript/rankers/online_apps/nav/nav.js`) has not been constructed or booted yet.
                // Once the `Nav` is booted a "booted" event is fired on the document.
                // We complete our `OnlineApp` boot process once we receive this event.
                document.addEventListener(NavConfig.bootedEventName, this.handleNavBooted.bind(this));

                // Construct the `OnlineAppNav` component.
                const insertedNavElement = rootElement.querySelector(OnlineAppNavConfig.selector);
                if (!!insertedNavElement) {
                    // This `OnlineAppNav` instance is *not* the same as the `Nav` discussed above.
                    this.onlineAppNav = new OnlineAppNav(insertedNavElement, this);
                    // Dispatch an event to signal that the nav has been constructed and assigned to `this.onlineAppNav`.
                    this.doc.dispatchEvent(new CustomEvent(OnlineAppNavConfig.constructedEventName));
                }

                // Because the HTML is available in the DOM we can now use it to extract the map filters and subpages.
                const mapFilters = (!!this.onlineAppNav) ? this.onlineAppNav.getMapFilters() : [];
                this.importMapFilters(mapFilters);
                const subpages = (!!this.onlineAppNav) ? this.onlineAppNav.getSubpages() : [];
                this.importSubpages(subpages);


                this.dispatchAction("navLoaded");
            } catch (error) {
                this.dispatchAction('navLoadFailed');
                console.error('[OnlineApp] Error loading navigation:', error);
                throw error; // Re-throw the error to be handled by the caller
            } finally {
                this.dispatchAction("loadNavEnded");
            }
        }
    }


    loadAuxiliaryResources() {
        if (this.auxiliaryScripts) {
            this.auxiliaryScripts.forEach( (script) => { injectLibrary(script) });
        }
        if (this.auxiliaryStylesheets) {
            this.auxiliaryStylesheets.forEach( (sheet) => { injectStylesheet(sheet) })
        }
    }

    handleNavBooted(event) {
        // The event detail refers to the `Nav` component (from `app/javascript/rankers/online_apps/nav/nav.js`).
        // this.nav = event.detail; // Not needed for now (probably best to avoid dependencies).
        this.dispatchAction('initializationEnded');
        this.boot();
    }

    boot() {
        this.handleInitialAppPath();
        this.handleRegionView();
        this.handlePositionView();
    }

    // Add the passed in map filter objects to our app's collection.
    importMapFilters(mapFilters) {
        this.mapFilterCollection.push(...mapFilters);
        return mapFilters;
    }

    // Add the passed in subpage objects to our app's collection.
    importSubpages(subpages) {
        this.subpageCollection.push(...subpages);
        return subpages;
    }


    // Add the passed in experience marker objects to our app's collection.
    importExperienceMarkers(experienceMarkers) {
        this.experienceMarkerCollection.push(...experienceMarkers);
        return experienceMarkers;
    }

    // Extract the experience objects from the passed in experience markers and add them to the app's collection.
    importExperiences(experienceMarkers){
        const experiences = experienceMarkers.map(experienceMarker => experienceMarker.experience);
        this.experienceCollection.push(...experiences);
        return experiences;
    }

    // TODO - Associate nav options that have an experience with the experience's in the collection.
    associateNavOptions(experiences) {
        return experiences;
    }

    constructMapLibsDependentComponents() {
        const rootElement = this.getRootElement();
        if (!rootElement) return;

        this.submarker = new OnlineAppSubmarker(null, this);

        const mapElement = rootElement.querySelector(OnlineAppMapConfig.selector)
        this.map = new OnlineAppMap(mapElement, this);
    }

    handleRegionView() {
        if (!this.regionCode) { return }
        if (!this.isExperienceMarkersLoaded()) { return }
        const regionExperienceMarkers = this.experienceMarkerCollection.allWithRegionCode(this.regionCode);
        if (regionExperienceMarkers.length >= 1) {
            this.map.zoomMapToFitExperienceMarkers(regionExperienceMarkers);
        }
    }

    handlePositionView() {
        if(!this.hasMapPosition() || !this.hasMapZoom()) { return }
        this.map.goToCoords(this.mapPosition.lat, this.mapPosition.long, this.mapZoom);
    }

    hasMapZoom() {
        return !!this.mapZoom;
    }

    hasMapPosition() {
        if ( (typeof this.mapPosition === 'undefined') || isEmpty(this.mapPosition) ) { return false }
        return !!this.mapPosition.lat && !!this.mapPosition.long;
    }


    // The initial app path is the page location's pathname without the base path - eg. '/filters/the-first-light').
    // On app boot this path is used to set state eg. select the appropriate nav option and experience marker.
    // The heavy lifting for this is just handled by the router - we pass the path to the router and the route handlers do their magic.
    // We resolve the path via the router - this runs the appropriate route handler without touching the browser location (or history).
    handleInitialAppPath() {
        this.onlineAppRouter.resolve(this.initialAppPath);
    }

    getPageTitleFromState() {
        const mapFilterTitle = !!this.currentMapFilter ? this.currentMapFilter.name : null;
        const experienceMarkerTitle = !!this.currentExperienceMarker ? this.currentExperienceMarker.primaryName : null;
        let components = new Collection().concat([this.basePageTitle, mapFilterTitle, experienceMarkerTitle]).compact();
        return components.join(' - ');
    }

    prefetch(url) {
        if (this.prefetcher.hasContent(url)) { return }
        this.prefetcher.addUrl(url);
    }

    isExperienceMarkersLoaded() {
        return this.readState('experienceMarkersLoaded');
    }

    handleStateChange(){
        this.currentMapFilter = this.readState('mapFilter');
        this.currentExperienceMarker = this.readState('experienceMarker');
        this.currentSubPageUrl = this.readState('auxResourceUrl');
        this.doc.title = this.getPageTitleFromState();
    }

    rankersAnalyticsEventTracker(eventName, options) {
        if (!this.tracker || !this.tracker.eventTracker) return;
        this.tracker.eventTracker(eventName, options);
    }

    // We remove the basePath from the current window location pathname to get the initial app path.
    // For example the location's pathname might be `/online/apps/camping_nz/filters/foo' and our basePath is `/online/apps/camping_nz`.
    // So the resulting initialAppPath would be `/filters/foo`.
    // This is used to set initial app state by sending it to our router.
    getInitialAppPath() {
        let path = this.env.location.pathname;
        if (!this.basePath || this.basePath === "") return path;

        const regex = new RegExp(`^${ escapeForRegExp(this.basePath) }`);
        return path.replace(regex,"");
    }

    getRootElement() {
        return this.root.element;
    }

    defaultMapFilter() {
        if (!this.mapFilterCollection) return null;
        if (this.defaultMapFilterCode) {
            const defaultMapFilter = this.mapFilterCollection.withFilterCode(this.defaultMapFilterCode);
            if (defaultMapFilter) return defaultMapFilter;
        }
        return this.mapFilterCollection.defaultMapFilter();
    }

}
